// [cursor 2026-06-01] S87-T4 finish-up: read Bookmarks from the backend service and
// refresh on the change bus. State surfaces a compact degraded mode when the
// backend/auth is unavailable so callers can render an honest fallback.
import { useCallback, useEffect, useState } from "react";
import backendClient from "../../lib/backend";
import type { BookmarkRecord } from "../../lib/services/bookmarks";
import {
  BOOKMARK_SAVE_EVENT,
  BOOKMARKS_CHANGED_EVENT,
  saveBookmark,
  type BookmarkSaveDetail,
} from "../../lib/bookmarks-bus";

export type BookmarksState = "loading" | "ready" | "degraded";

export function useBookmarks(options?: { limit?: number }) {
  const [items, setItems] = useState<BookmarkRecord[]>([]);
  const [state, setState] = useState<BookmarksState>("loading");
  const limit = options?.limit;

  const refresh = useCallback(async () => {
    try {
      const list = await backendClient.bookmarks.list(
        limit ? { limit } : undefined,
      );
      setItems(list);
      setState("ready");
    } catch {
      setState("degraded");
    }
  }, [limit]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const handler = () => void refresh();
    window.addEventListener(BOOKMARKS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(BOOKMARKS_CHANGED_EVENT, handler);
  }, [refresh]);

  return { items, state, refresh };
}

/**
 * Single app-level sink for the legacy `fintheon:bookmark-save` event seam.
 * Mount once (MainLayout). Routes queued bookmark events to the durable service.
 */
export function useBookmarkSink(): void {
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | (BookmarkSaveDetail & { metadata?: Record<string, unknown> })
        | undefined;
      if (!detail?.sourceType || !detail?.sourceId || !detail?.title) return;
      void saveBookmark(detail).catch(() => undefined);
    };
    window.addEventListener(BOOKMARK_SAVE_EVENT, handler);
    return () => window.removeEventListener(BOOKMARK_SAVE_EVENT, handler);
  }, []);
}
